import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CrmRow } from "../components/CrmRow.jsx";
import { ListEmpty } from "../components/ListEmpty.jsx";
import { LoadingBlock } from "../components/LoadingBlock.jsx";
import { ClientCardModal } from "../components/ClientCardModal.jsx";
import { BottomSheetModal } from "../components/BottomSheetModal.jsx";
import { getChatPath } from "../../utils/nav.js";
import { fmtDt } from "../../utils/users.js";
import { apiFetch, apiJson, errorFromResponse } from "../../utils/api.js";
import { validateEmail, validatePhone } from "../../utils/validation.js";
import { DateTime } from "luxon";
import { useMe } from "../../hooks/useMe.js";
import { TransferManagerSheet } from "../components/TransferManagerSheet.jsx";
import { InlineBanner } from "../components/InlineBanner.jsx";

const TABS = ["main", "history", "tasks", "comments"];
const MODALS_DEAL = [
  "client",
  "createTask",
  "task",
  "qualification",
  "qualificationView",
  "refusal",
  "contractUpload",
  "assignDealer",
  "addCustomField",
  "editCustomField",
  "transferManager",
];

const CUSTOM_FIELD_TYPES = [
  { value: "text", label: "Текст" },
  { value: "phone", label: "Телефон" },
  { value: "email", label: "Почта" },
  { value: "number", label: "Число" },
  { value: "date", label: "Дата" },
];

function customFieldTypeIsMultiline(fieldType) {
  return String(fieldType || "text").toLowerCase() === "text";
}

/** Многострочное поле: высота по содержимому, с ограничением по max-height */
function DealCustomFieldTextArea({
  className,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  maxHeightPx = 360,
}) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(Math.max(el.scrollHeight, 36), maxHeightPx);
    el.style.height = `${h}px`;
  }, [maxHeightPx]);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  return (
    <textarea
      ref={ref}
      rows={1}
      className={className}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => {
        onChange(e);
        requestAnimationFrame(resize);
      }}
      onBlur={onBlur}
    />
  );
}

function isoToLocalInputValue(iso, tz) {
  if (!iso || !tz) return "";
  const dt = DateTime.fromISO(String(iso).replace("Z", "+00:00"), { zone: "utc" });
  if (!dt.isValid) return "";
  return dt.setZone(tz).toFormat("yyyy-MM-dd'T'HH:mm");
}

function localInputValueToIso(value, tz) {
  if (!value || !tz) return null;
  const dt = DateTime.fromFormat(value, "yyyy-MM-dd'T'HH:mm", { zone: tz });
  if (!dt.isValid) return null;
  return dt.toUTC().toISO();
}

export function DealCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canTransferAssignments, me } = useMe();

  const tab = useMemo(() => {
    const t = searchParams.get("tab") || "";
    return TABS.includes(t) ? t : "main";
  }, [searchParams]);
  const modal = searchParams.get("modal") || "";
  const taskIdParam = searchParams.get("task") || "";

  const setTab = useCallback(
    (t) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (TABS.includes(t)) p.set("tab", t);
          else p.delete("tab");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const openModal = useCallback(
    (name, taskId = null) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (MODALS_DEAL.includes(name)) {
            p.set("modal", name);
            if (taskId != null && taskId !== "") p.set("task", String(taskId));
            else p.delete("task");
          } else {
            p.delete("modal");
            p.delete("task");
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const closeModal = useCallback(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("modal");
        p.delete("task");
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /** Время на карточке — в TZ отдела назначенного менеджера (с сервера). */
  const dealTz = useMemo(
    () => data?.deal?.iana_timezone || me?.iana_timezone || "Europe/Moscow",
    [data?.deal?.iana_timezone, me?.iana_timezone],
  );

  const [localFunnelId, setLocalFunnelId] = useState("");
  const [nextStageBusy, setNextStageBusy] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskDueAt, setNewTaskDueAt] = useState("");
  const [taskSubmitBusy, setTaskSubmitBusy] = useState(false);
  const [taskDetailDueAt, setTaskDetailDueAt] = useState("");
  const [taskDetailTitle, setTaskDetailTitle] = useState("");
  const [taskDetailDescription, setTaskDetailDescription] = useState("");
  const [taskDetailDone, setTaskDetailDone] = useState(false);
  const [taskDetailSaveBusy, setTaskDetailSaveBusy] = useState(false);
  const [pendingStageId, setPendingStageId] = useState("");
  const [qualificationForm, setQualificationForm] = useState({
    budget: "",
    deadlines: "",
    brand: "",
    country: "",
    year: "",
    trim: "",
    mileage: "",
    condition: "",
    comment: "",
  });
  const [qualificationSubmitBusy, setQualificationSubmitBusy] = useState(false);
  const [qualificationErrors, setQualificationErrors] = useState({});
  const [refusalReasons, setRefusalReasons] = useState([]);
  const [refusalReasonId, setRefusalReasonId] = useState(null);
  const [refusalBusy, setRefusalBusy] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [contractUploadBusy, setContractUploadBusy] = useState(false);
  const [dealersList, setDealersList] = useState([]);
  const [assignDealerId, setAssignDealerId] = useState(null);
  const [assignDealerBusy, setAssignDealerBusy] = useState(false);
  const [dealTitleEditing, setDealTitleEditing] = useState(false);
  const [dealTitleValue, setDealTitleValue] = useState("");
  const [dealTitleSaveBusy, setDealTitleSaveBusy] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldSaveBusy, setCustomFieldSaveBusy] = useState(false);
  const [addCustomFieldBusy, setAddCustomFieldBusy] = useState(false);
  const [newCustomFieldLabel, setNewCustomFieldLabel] = useState("");
  const [newCustomFieldType, setNewCustomFieldType] = useState("text");
  const [editCustomFieldId, setEditCustomFieldId] = useState(null);
  const [editCustomFieldLabel, setEditCustomFieldLabel] = useState("");
  const [editCustomFieldType, setEditCustomFieldType] = useState("text");
  const [customFieldDefBusy, setCustomFieldDefBusy] = useState(false);
  const [dealPhone, setDealPhone] = useState("");
  const [dealEmail, setDealEmail] = useState("");
  const [dealContactSaveBusy, setDealContactSaveBusy] = useState(false);
  const [calling, setCalling] = useState(false);
  /** Пока грузится blob записи Телфин для скачивания (по rec_id). */
  const [telphinDownloadingRecId, setTelphinDownloadingRecId] = useState(null);
  /** Встроенные уведомления (звонок, ошибки действий) вместо window.alert. */
  const [dealBanner, setDealBanner] = useState(null);
  const showDealBanner = useCallback((message, variant = "error") => {
    if (message == null || message === "") {
      setDealBanner(null);
      return;
    }
    setDealBanner({ message: String(message), variant });
  }, []);
  const [dealPhoneError, setDealPhoneError] = useState("");
  const [dealEmailError, setDealEmailError] = useState("");
  const [customFieldErrors, setCustomFieldErrors] = useState({});
  const clientCardOpen = modal === "client";
  const assignDealerOpen = modal === "assignDealer";
  const transferManagerOpen = modal === "transferManager";
  const showTaskForm = modal === "createTask";
  const selectedTask = useMemo(() => {
    if (modal !== "task" || !taskIdParam || !data?.tasks) return null;
    const tid = parseInt(taskIdParam, 10);
    if (Number.isNaN(tid)) return null;
    return data.tasks.find((t) => t.id === tid) || null;
  }, [modal, taskIdParam, data?.tasks]);

  useEffect(() => {
    if (selectedTask) {
      setTaskDetailTitle(selectedTask.title ?? "");
      setTaskDetailDescription(selectedTask.description ?? "");
      setTaskDetailDueAt(isoToLocalInputValue(selectedTask.dueAt, dealTz));
      setTaskDetailDone(!!selectedTask.done);
    } else {
      setTaskDetailTitle("");
      setTaskDetailDescription("");
      setTaskDetailDueAt("");
      setTaskDetailDone(false);
    }
  }, [selectedTask, dealTz]);

  const qualificationOpen = modal === "qualification";
  const refusalOpen = modal === "refusal";
  const contractUploadOpen = modal === "contractUpload";
  const qualificationRequiredStage = data?.qualification_required_stage ?? "op_qualified";
  const qualificationFields = data?.qualification_fields ?? [];
  useEffect(() => {
    if (qualificationOpen && data?.qualification) {
      const q = data.qualification;
      setQualificationForm({
        budget: q.budget ?? "",
        deadlines: q.deadlines ?? "",
        brand: q.brand ?? "",
        country: q.country ?? "",
        year: q.year != null ? String(q.year) : "",
        trim: q.trim ?? "",
        mileage: q.mileage ?? "",
        condition: q.condition ?? "",
        comment: q.comment ?? "",
      });
    } else if (qualificationOpen) {
      setQualificationForm({
        budget: "",
        deadlines: "",
        brand: "",
        country: "",
        year: "",
        trim: "",
        mileage: "",
        condition: "",
        comment: "",
      });
      setQualificationErrors({});
    }
  }, [qualificationOpen, data?.qualification]);

  useEffect(() => {
    const fields = data?.custom_fields ?? [];
    setCustomFieldValues((prev) => {
      const next = { ...prev };
      fields.forEach((f) => {
        next[f.id] = f.value ?? "";
      });
      return next;
    });
  }, [data?.custom_fields]);

  useEffect(() => {
    const d = data?.deal;
    if (d) {
      const p = d.phone;
      const e = d.email;
      setDealPhone(p === "—" || p == null ? "" : String(p));
      setDealEmail(e === "—" || e == null ? "" : String(e));
      setDealTitleValue(d.title != null ? String(d.title) : (d.clientName || ""));
    }
  }, [data?.deal]);

  useEffect(() => {
    if (!refusalOpen) {
      setRefusalReasonId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson("/api/rejection-reasons");
        if (!cancelled && res?.items) setRefusalReasons(res.items);
        else if (!cancelled) setRefusalReasons([]);
      } catch (_) {
        if (!cancelled) setRefusalReasons([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refusalOpen]);

  const refetchDeal = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiJson(`/api/deals/${encodeURIComponent(id)}`);
      setData(res);
    } catch (_) {}
  }, [id]);

  const saveDealTitle = useCallback(
    async (newTitle) => {
      if (id == null) return;
      setDealTitleSaveBusy(true);
      try {
        await apiJson(`/api/deals/${encodeURIComponent(id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: newTitle != null && String(newTitle).trim() ? String(newTitle).trim() : null }),
        });
        setDealTitleEditing(false);
        await refetchDeal();
      } catch (e) {
        showDealBanner(e?.message || "Не удалось сохранить название", "error");
      } finally {
        setDealTitleSaveBusy(false);
      }
    },
    [id, refetchDeal, showDealBanner]
  );

  const handleTaskPatchRequireAction = useCallback(
    (data) => {
      const detail = data?.detail || {};
      if (detail.require_action === "message_to_client" && detail.user_id) {
        closeModal();
        navigate(getChatPath(detail.user_id, "/deals"));
        return true;
      }
      if (detail.require_action === "no_telegram" && detail.message) {
        showDealBanner(detail.message, "warning");
        return true;
      }
      if (detail.require_action === "qualification") {
        closeModal();
        setPendingStageId(qualificationRequiredStage);
        openModal("qualification");
        return true;
      }
      if (detail.require_action === "contract_upload") {
        closeModal();
        openModal("contractUpload");
        return true;
      }
      return false;
    },
    [closeModal, navigate, openModal, qualificationRequiredStage, setPendingStageId, showDealBanner]
  );

  const saveTaskDetail = useCallback(async () => {
    if (!id || !selectedTask?.id || !taskDetailTitle.trim()) return;
    setTaskDetailSaveBusy(true);
    try {
      const payload = {
        title: taskDetailTitle.trim(),
        description: taskDetailDescription.trim() || null,
        due_at: localInputValueToIso(taskDetailDueAt, dealTz),
        done: taskDetailDone,
      };
      const res = await apiFetch(`/api/deals/${id}/tasks/${selectedTask.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok && res.status === 400) {
        const data = await res.json().catch(() => ({}));
        if (handleTaskPatchRequireAction(data)) return;
      }
      if (!res.ok) throw new Error(await res.text());
      await refetchDeal();
      closeModal();
    } finally {
      setTaskDetailSaveBusy(false);
    }
  }, [id, selectedTask?.id, taskDetailTitle, taskDetailDescription, taskDetailDueAt, taskDetailDone, refetchDeal, handleTaskPatchRequireAction, closeModal]);

  const markTaskDone = useCallback(async () => {
    if (!id || !selectedTask?.id || !taskDetailTitle.trim()) return;
    setTaskDetailSaveBusy(true);
    try {
      const payload = {
        title: taskDetailTitle.trim(),
        description: taskDetailDescription.trim() || null,
        due_at: localInputValueToIso(taskDetailDueAt, dealTz),
        done: true,
      };
      const res = await apiFetch(`/api/deals/${id}/tasks/${selectedTask.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok && res.status === 400) {
        const data = await res.json().catch(() => ({}));
        if (handleTaskPatchRequireAction(data)) return;
      }
      if (!res.ok) throw new Error(await res.text());
      await refetchDeal();
      closeModal();
    } finally {
      setTaskDetailSaveBusy(false);
    }
  }, [id, selectedTask?.id, taskDetailTitle, taskDetailDescription, taskDetailDueAt, refetchDeal, closeModal, handleTaskPatchRequireAction]);

  const handleRefuse = useCallback(async () => {
    if (!id || refusalReasonId == null) return;
    setRefusalBusy(true);
    try {
      await apiJson(`/api/deals/${id}/refuse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason_id: refusalReasonId }),
      });
      closeModal();
      await refetchDeal();
    } catch (e) {
      showDealBanner(e?.message || "Не удалось оформить отказ", "error");
    } finally {
      setRefusalBusy(false);
    }
  }, [id, refusalReasonId, closeModal, refetchDeal, showDealBanner]);

  const handleContractUpload = async (e) => {
    e.preventDefault();
    if (!id || !contractFile) return;
    setContractUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", contractFile);
      form.append("stage_id", "op_contract_signed");
      const res = await apiFetch(`/api/deals/${id}/documents`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Ошибка загрузки");
      }
      setContractFile(null);
      closeModal();
      await refetchDeal();
    } catch (err) {
      showDealBanner(err?.message || "Не удалось загрузить файл", "error");
    } finally {
      setContractUploadBusy(false);
    }
  };

  const downloadDocument = useCallback(
    async (documentId, fileName) => {
      if (!id) return;
      try {
        const res = await apiFetch(`/api/deals/${id}/documents/${documentId}/file`);
        if (!res.ok) throw new Error("Не удалось скачать");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "document";
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        showDealBanner(e?.message || "Ошибка скачивания", "error");
      }
    },
    [id, showDealBanner]
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiJson(`/api/deals/${encodeURIComponent(id)}`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Сделка не найдена");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const deal = data?.deal ?? data;
  const funnels = data?.funnels ?? [];
  const stages = data?.stages ?? [];
  const isDealerView = !!deal?.isDealerView;
  useEffect(() => {
    if (!deal) return;
    setLocalFunnelId(deal.funnelId ?? "");
  }, [deal?.funnelId]);

  useEffect(() => {
    if (!assignDealerOpen) return;
    let cancelled = false;
    apiJson("/api/dealers")
      .then((res) => {
        if (!cancelled && res?.items) setDealersList(res.items);
      })
      .catch(() => {
        if (!cancelled) setDealersList([]);
      });
    return () => { cancelled = true; };
  }, [assignDealerOpen]);

  const patchFunnel = useCallback(
    async (funnelId) => {
      if (!id || !funnelId) return;
      try {
        await apiJson(`/api/deals/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ funnel_id: funnelId }),
        });
        setLocalFunnelId(funnelId);
        await refetchDeal();
      } catch (_) {
        setLocalFunnelId(deal?.funnelId ?? "");
      }
    },
    [id, deal?.funnelId, refetchDeal]
  );

  const handleAssignDealer = useCallback(
    async (e) => {
      e.preventDefault();
      if (!id) return;
      setAssignDealerBusy(true);
      try {
        await apiJson(`/api/deals/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assigned_dealer_id: assignDealerId || null }),
        });
        await refetchDeal();
        closeModal();
      } catch (err) {
        showDealBanner(err?.message || "Не удалось назначить дилера", "error");
      } finally {
        setAssignDealerBusy(false);
      }
    },
    [id, assignDealerId, refetchDeal, closeModal, showDealBanner]
  );

  const goNextStage = useCallback(() => {
    if (!id || !data?.nextStageId) return;
    const nextId = data.nextStageId;
    if (nextId === qualificationRequiredStage && !data?.qualification) {
      setPendingStageId(nextId);
      openModal("qualification");
      return;
    }
    // Переход в «Подборка отправлена» — по кнопке «Следующий этап»; задача «Отправить предложение» отмечается выполненной на бэкенде.
    if (nextId === (data?.contractSignedStageId || "op_contract_signed")) {
      openModal("contractUpload");
      return;
    }
    setNextStageBusy(true);
    apiJson(`/api/deals/${id}/next-stage`, { method: "POST" })
      .then(() => refetchDeal())
      .finally(() => setNextStageBusy(false));
  }, [id, data?.nextStageId, data?.qualification, qualificationRequiredStage, openModal, refetchDeal]);

  const validateQualificationForm = () => {
    const q = qualificationForm;
    const err = {};
    if (!(q.budget || "").trim()) err.budget = "Укажите бюджет";
    if (!(q.deadlines || "").trim()) err.deadlines = "Укажите сроки";
    if (!(q.brand || "").trim()) err.brand = "Укажите марку";
    if (!(q.country || "").trim()) err.country = "Укажите страну";
    if (!(q.year || "").trim()) err.year = "Укажите год авто";
    if (!(q.trim || "").trim()) err.trim = "Укажите комплектацию";
    if (!(q.mileage || "").trim()) err.mileage = "Укажите пробег";
    if (!(q.condition || "").trim()) err.condition = "Выберите состояние авто";
    setQualificationErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleQualificationSubmit = async (e) => {
    e.preventDefault();
    if (!id || !pendingStageId) return;
    if (!validateQualificationForm()) return;
    const q = qualificationForm;
    setQualificationSubmitBusy(true);
    try {
      await apiJson(`/api/deals/${id}/qualification`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          budget: q.budget.trim(),
          deadlines: q.deadlines.trim(),
          brand: q.brand.trim(),
          country: q.country.trim(),
          year: q.year.trim(),
          trim: q.trim.trim(),
          mileage: q.mileage.trim(),
          condition: q.condition.trim(),
          comment: q.comment?.trim() || undefined,
        }),
      });
      await apiJson(`/api/deals/${id}/next-stage`, { method: "POST" });
      setPendingStageId("");
      closeModal();
      await refetchDeal();
    } finally {
      setQualificationSubmitBusy(false);
    }
  };

  const saveCustomFieldValue = useCallback(
    async (fieldId, value) => {
      if (!id) return;
      setCustomFieldErrors((prev) => ({ ...prev, [String(fieldId)]: "" }));
      const field = data?.custom_fields?.find((f) => f.id === fieldId);
      const valueStr = typeof value === "string" ? value.trim() : "";
      if (field?.type === "phone" && valueStr) {
        const r = validatePhone(valueStr);
        if (!r.valid) {
          setCustomFieldErrors((prev) => ({ ...prev, [String(fieldId)]: r.message || "Некорректный формат" }));
          return;
        }
      }
      if (field?.type === "email" && valueStr) {
        const r = validateEmail(valueStr);
        if (!r.valid) {
          setCustomFieldErrors((prev) => ({ ...prev, [String(fieldId)]: r.message || "Некорректный формат" }));
          return;
        }
      }
      setCustomFieldSaveBusy(true);
      try {
        await apiJson(`/api/deals/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            custom_field_values: { [String(fieldId)]: value },
          }),
        });
        await refetchDeal();
      } finally {
        setCustomFieldSaveBusy(false);
      }
    },
    [id, data?.custom_fields, refetchDeal]
  );

  const saveDealContact = useCallback(
    async (phone, email) => {
      const uid = data?.deal?.user_id;
      if (uid == null) return;
      setDealPhoneError("");
      setDealEmailError("");
      const phoneTrim = typeof phone === "string" ? phone.trim() : "";
      const emailTrim = typeof email === "string" ? email.trim() : "";
      if (phoneTrim) {
        const r = validatePhone(phoneTrim);
        if (!r.valid) {
          setDealPhoneError(r.message || "Некорректный формат");
          return;
        }
      }
      if (emailTrim) {
        const r = validateEmail(emailTrim);
        if (!r.valid) {
          setDealEmailError(r.message || "Некорректный формат");
          return;
        }
      }
      setDealContactSaveBusy(true);
      try {
        await apiJson("/api/user", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: uid,
            phone: phoneTrim || null,
            email: emailTrim || null,
          }),
        });
        await refetchDeal();
      } finally {
        setDealContactSaveBusy(false);
      }
    },
    [data?.deal?.user_id, refetchDeal]
  );

  const openAddCustomFieldModal = useCallback(() => {
    setNewCustomFieldLabel("");
    setNewCustomFieldType("text");
    openModal("addCustomField");
  }, [openModal]);

  const handleAddCustomFieldSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!newCustomFieldLabel.trim()) return;
      setAddCustomFieldBusy(true);
      try {
        await apiJson(`/api/deals/${id}/custom-fields`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            label: newCustomFieldLabel.trim(),
            type: newCustomFieldType,
          }),
        });
        closeModal();
        setNewCustomFieldLabel("");
        setNewCustomFieldType("text");
        await refetchDeal();
      } finally {
        setAddCustomFieldBusy(false);
      }
    },
    [id, newCustomFieldLabel, newCustomFieldType, closeModal, refetchDeal]
  );

  const openEditCustomFieldModal = useCallback(
    (f) => {
      setEditCustomFieldId(f.id);
      setEditCustomFieldLabel((f.label || "").trim());
      setEditCustomFieldType((f.type || "text").toLowerCase());
      openModal("editCustomField");
    },
    [openModal]
  );

  const handleEditCustomFieldSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (editCustomFieldId == null || !editCustomFieldLabel.trim()) return;
      setCustomFieldDefBusy(true);
      try {
        await apiJson(`/api/deals/${id}/custom-fields/${editCustomFieldId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            label: editCustomFieldLabel.trim(),
            type: editCustomFieldType,
          }),
        });
        closeModal();
        setEditCustomFieldId(null);
        await refetchDeal();
      } catch (err) {
        showDealBanner(err?.message || "Не удалось сохранить поле", "error");
      } finally {
        setCustomFieldDefBusy(false);
      }
    },
    [id, editCustomFieldId, editCustomFieldLabel, editCustomFieldType, closeModal, refetchDeal, showDealBanner]
  );

  const handleDeleteCustomField = useCallback(
    async (field) => {
      if (!field?.id) return;
      const ok = window.confirm(`Удалить поле «${field.label}» у этой сделки?`);
      if (!ok) return;
      setCustomFieldDefBusy(true);
      try {
        await apiJson(`/api/deals/${id}/custom-fields/${field.id}`, { method: "DELETE" });
        setCustomFieldValues((prev) => {
          const next = { ...prev };
          delete next[field.id];
          return next;
        });
        setCustomFieldErrors((prev) => {
          const next = { ...prev };
          delete next[String(field.id)];
          return next;
        });
        await refetchDeal();
      } catch (err) {
        showDealBanner(err?.message || "Не удалось удалить поле", "error");
      } finally {
        setCustomFieldDefBusy(false);
      }
    },
    [id, refetchDeal, showDealBanner]
  );

  const getCustomFieldInputType = (fieldType) => {
    const t = (fieldType || "text").toLowerCase();
    if (t === "phone") return "tel";
    if (t === "email") return "email";
    if (t === "number") return "number";
    if (t === "date") return "date";
    return "text";
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!id || !newTaskTitle.trim()) return;
    setTaskSubmitBusy(true);
    try {
      const body = {
        title: newTaskTitle.trim(),
        description: newTaskDescription.trim() || undefined,
      };
      if (newTaskDueAt) body.due_at = localInputValueToIso(newTaskDueAt, dealTz);
      await apiJson(`/api/deals/${id}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await refetchDeal();
      setNewTaskTitle("");
      setNewTaskDescription("");
      setNewTaskDueAt("");
      closeModal();
    } finally {
      setTaskSubmitBusy(false);
    }
  };

  const normalizeDialPhone = useCallback((raw) => {
    const src = String(raw || "").trim();
    if (!src || src === "—") return "";
    const hasPlus = src.startsWith("+");
    const digits = src.replace(/\D/g, "");
    if (!digits) return "";
    return hasPlus ? `+${digits}` : digits;
  }, []);

  const callClient = useCallback(async () => {
    const rawPhone = isDealerView ? deal?.phone : dealPhone;
    const dstNum = normalizeDialPhone(rawPhone);
    if (!dstNum) {
      showDealBanner(
        "У клиента не указан телефон для звонка. Укажите номер в поле «Телефон» на этой вкладке.",
        "warning"
      );
      return;
    }
    showDealBanner(null);
    setCalling(true);
    try {
      await apiJson("/api/calls/callback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dst_num: dstNum }),
      });
      showDealBanner(
        "Звонок отправлен в АТС. Примите входящий вызов на софтфоне или телефоне.",
        "success"
      );
    } catch (err) {
      showDealBanner(err?.message || "Не удалось инициировать звонок", "error");
    } finally {
      setCalling(false);
    }
  }, [deal?.phone, dealPhone, isDealerView, normalizeDialPhone, showDealBanner]);

  useEffect(() => {
    if (!dealBanner) return;
    const t = window.setTimeout(() => setDealBanner(null), 8000);
    return () => window.clearTimeout(t);
  }, [dealBanner]);

  const downloadTelphinRecording = useCallback(
    async (recId) => {
      if (!id || !recId) return;
      const rid = String(recId);
      setTelphinDownloadingRecId(rid);
      try {
        const qs = new URLSearchParams({ rec_id: rid });
        const res = await apiFetch(`/api/deals/${encodeURIComponent(id)}/telphin-recording?${qs}`);
        if (!res.ok) throw await errorFromResponse(res);
        const blob = await res.blob();
        let filename = "telphin-recording.mp3";
        const cd = res.headers.get("Content-Disposition");
        if (cd) {
          const m =
            cd.match(/filename\*=UTF-8''([^;]+)/i) ||
            cd.match(/filename="([^"]+)"/i) ||
            cd.match(/filename=([^;\s]+)/i);
          if (m) {
            try {
              filename = decodeURIComponent(m[1].replace(/^["']|["']$/g, ""));
            } catch {
              filename = m[1];
            }
          }
        }
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        showDealBanner(e instanceof Error ? e.message : String(e), "error");
      } finally {
        setTelphinDownloadingRecId(null);
      }
    },
    [id, showDealBanner]
  );

  if (loading && !data) {
    return <LoadingBlock />;
  }

  if (error || !data) {
    return (
      <main className="chat">
        <div className="section">
          <div className="sectionTitle" style={{ color: "var(--colorErr)" }}>
            {error || "Сделка не найдена"}
          </div>
          <button
            type="button"
            className="linkBtn"
            onClick={() => navigate("/deals")}
          >
            ← К списку сделок
          </button>
        </div>
      </main>
    );
  }

  const history = data.history ?? [];
  const tasks = data.tasks ?? [];
  const dealComments = data.comments ?? [];
  const tasksIncomplete = tasks.filter((t) => t.done !== true);
  const tasksNoDue = tasksIncomplete.filter((t) => !t.dueAt);
  const tasksWithDue = tasksIncomplete.filter((t) => !!t.dueAt);
  const clientTgId = deal.user_telegram_id;
  const clientCrmId = deal.user_id;

  return (
    <>
      <main className="chat dealCardPage">
        {!isDealerView && (
          <div className="dealCardTabsWrap">
            <div className="reportsTabs">
              <button
                type="button"
                className={`reportsTab ${tab === "main" ? "active" : ""}`}
                onClick={() => setTab("main")}
              >
                Основное
              </button>
              <button
                type="button"
                className={`reportsTab ${tab === "history" ? "active" : ""}`}
                onClick={() => setTab("history")}
              >
                История
              </button>
              <button
                type="button"
                className={`reportsTab ${tab === "tasks" ? "active" : ""}`}
                onClick={() => setTab("tasks")}
              >
                Задачи
              </button>
              <button
                type="button"
                className={`reportsTab ${tab === "comments" ? "active" : ""}`}
                onClick={() => setTab("comments")}
              >
                Комментарии
              </button>
            </div>
          </div>
        )}

        <div className={`dealCardContent screenScroll ${tab === "main" || isDealerView ? "dealCardContentWithFooter" : ""}`}>
        {dealBanner ? (
          <InlineBanner
            message={dealBanner.message}
            variant={dealBanner.variant}
            onClose={() => setDealBanner(null)}
            className="dealCardTopBanner"
          />
        ) : null}
        {(tab === "main" || isDealerView) && (
          <>
          <div className="dealCardActionsRow">
            <button
              type="button"
              className="linkBtn dealCardActionBtn"
              onClick={() => openModal("client")}
            >
              Карточка клиента
            </button>
            <button
              type="button"
              className="linkBtn dealCardActionBtn"
              onClick={() => {
                if (clientCrmId == null || clientCrmId <= 0) {
                  showDealBanner("Нет клиента в CRM — чат недоступен.", "warning");
                  return;
                }
                navigate(getChatPath(clientCrmId, "/deals"));
              }}
            >
              Чат
            </button>
            {!isDealerView ? (
              <button
                type="button"
                className="linkBtn dealCardActionBtn dealCardActionIconBtn"
                onClick={callClient}
                title="Позвонить клиенту"
                aria-label="Позвонить клиенту"
                disabled={calling}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.08 4.18 2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.62a2 2 0 0 1-.45 2.11L9.1 9.62a16 16 0 0 0 5.28 5.28l1.17-1.19a2 2 0 0 1 2.11-.45c.84.33 1.72.56 2.62.68A2 2 0 0 1 22 16.92z" />
                </svg>
              </button>
            ) : null}
          </div>
          <div className="section">
            <div className="crmListSurface">
              <div className="crmCardHeader">
                <div className="crmCardHeaderTop">
                  {isDealerView ? (
                    <div className="crmCardTitle">{deal.clientName}</div>
                  ) : dealTitleEditing ? (
                    <input
                      type="text"
                      className="input dealEditableInput crmCardTitleInput"
                      value={dealTitleValue}
                      onChange={(e) => setDealTitleValue(e.target.value)}
                      onBlur={() => saveDealTitle(dealTitleValue)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                        if (e.key === "Escape") {
                          setDealTitleValue(deal.title != null ? deal.title : deal.clientName || "");
                          setDealTitleEditing(false);
                        }
                      }}
                      disabled={dealTitleSaveBusy}
                      autoFocus
                      placeholder="Название сделки"
                      aria-label="Название сделки"
                    />
                  ) : (
                    <button
                      type="button"
                      className="crmCardTitle crmCardTitleBtn"
                      onClick={() => {
                        setDealTitleValue(deal.title != null ? deal.title : deal.clientName || "");
                        setDealTitleEditing(true);
                      }}
                      title="Изменить название"
                    >
                      {deal.clientName}
                    </button>
                  )}
                  {!isDealerView && (
                    <div className="dealStageCurrent">
                      <span className="dealStageLabel">Этап:</span>
                      <span className="dealStageValue">{deal.stageTitle ?? deal.stageId}</span>
                    </div>
                  )}
                </div>
                <div className="crmCardSubtitleRow">
                  <span className="crmCardSubtitle">Сделка #{String(id || deal.id)}</span>
                  {!isDealerView && funnels.length ? (
                    <select
                      className="dealStageSelect dealFunnelSelect"
                      value={localFunnelId}
                      onChange={(e) => patchFunnel(e.target.value)}
                      title="Воронка"
                      aria-label="Воронка"
                    >
                      {funnels.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.title}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
              </div>

              {!isDealerView && (
                <div className="dealCustomFieldsBlock">
                  <div className="dealCustomFieldsHeader">
                    <span className="dealCustomFieldsTitle">Дополнительные поля</span>
                    <button
                      type="button"
                      className="linkBtn dealAddCustomFieldPlusBtn"
                      onClick={openAddCustomFieldModal}
                      title="Добавить поле"
                      aria-label="Добавить поле"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {!isDealerView ? (
                <>
                  <div className="dealFieldWrap">
                    <div className="crmKvRow dealCustomFieldRow">
                      <div className="crmKvLabel">Телефон</div>
                      <input
                        type="tel"
                        className={`input dealEditableInput ${dealPhoneError ? "dealEditableInputError" : ""}`}
                        value={dealPhone}
                        onChange={(e) => {
                          setDealPhone(e.target.value);
                          setDealPhoneError("");
                        }}
                        onBlur={(e) => saveDealContact(e.target.value.trim(), dealEmail)}
                        placeholder="Телефон"
                        disabled={dealContactSaveBusy}
                      />
                    </div>
                    {dealPhoneError ? <div className="dealFieldError">{dealPhoneError}</div> : null}
                  </div>
                  <div className="dealFieldWrap">
                    <div className="crmKvRow dealCustomFieldRow">
                      <div className="crmKvLabel">Email</div>
                      <input
                        type="email"
                        className={`input dealEditableInput ${dealEmailError ? "dealEditableInputError" : ""}`}
                        value={dealEmail}
                        onChange={(e) => {
                          setDealEmail(e.target.value);
                          setDealEmailError("");
                        }}
                        onBlur={(e) => saveDealContact(dealPhone, e.target.value.trim())}
                        placeholder="Email"
                        disabled={dealContactSaveBusy}
                      />
                    </div>
                    {dealEmailError ? <div className="dealFieldError">{dealEmailError}</div> : null}
                  </div>
                </>
              ) : (
                <>
                  <div className="crmKvRow">
                    <div className="crmKvLabel">Телефон</div>
                    <div className="crmKvValue" title={deal.phone}>
                      {deal.phone}
                    </div>
                  </div>
                  <div className="crmKvRow">
                    <div className="crmKvLabel">Email</div>
                    <div className="crmKvValue" title={deal.email}>
                      {deal.email}
                    </div>
                  </div>
                </>
              )}
              {!isDealerView && (
                <div className="crmKvRow crmKvRowWithAction">
                  <div className="crmKvLabel">Менеджер</div>
                  <div className="crmKvValue" title={deal.managerName}>
                    {deal.managerName}
                  </div>
                  {canTransferAssignments ? (
                    <button
                      type="button"
                      className="linkBtn dealAssignDealerBtn"
                      onClick={() => openModal("transferManager")}
                      title="Передать сделку другому менеджеру"
                    >
                      Передать
                    </button>
                  ) : null}
                </div>
              )}
              {false && !isDealerView && (
                <div className="crmKvRow crmKvRowWithAction">
                  <div className="crmKvLabel">Дилер</div>
                  <div className="crmKvValue" title={deal.assignedDealerName || "—"}>
                    {deal.assignedDealerName || "—"}
                  </div>
                  <button
                    type="button"
                    className="linkBtn dealAssignDealerBtn"
                    onClick={() => {
                      setAssignDealerId(deal.assignedDealerId ?? null);
                      openModal("assignDealer");
                    }}
                    title="Дать доступ дилеру к чату с клиентом"
                  >
                    Назначить
                  </button>
                </div>
              )}
              {data?.qualification ? (
                <div className="crmKvRow crmKvRowWithAction">
                  <div className="crmKvLabel">Квалификация</div>
                  <button
                    type="button"
                    className="dealCustomFieldIconBtn dealQualificationEyeBtn"
                    onClick={() => openModal("qualificationView")}
                    title="Показать квалификацию"
                    aria-label="Показать квалификацию"
                  >
                    <svg className="dealQualificationEyeIcon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              ) : null}
              {!isDealerView && (data?.custom_fields ?? []).length > 0 ? (
                <div className="dealCustomFieldsList">
                  {(data?.custom_fields ?? []).map((f) => {
                    const fieldError = customFieldErrors[f.id];
                    const multiline = customFieldTypeIsMultiline(f.type);
                    const inputClass = `input dealEditableInput dealCustomFieldValueControl ${fieldError ? "dealEditableInputError" : ""}`;
                    return (
                      <div key={f.id} className="dealFieldWrap dealCustomFieldWrap">
                        <div className="dealCustomFieldStack">
                          <div className="crmKvRow crmKvRowWithAction dealCustomFieldTitleRow">
                            <button
                              type="button"
                              className="crmKvLabel dealCustomFieldNameEditBtn"
                              onClick={() => openEditCustomFieldModal(f)}
                              disabled={customFieldDefBusy}
                              title="Редактировать название и тип"
                              aria-label={`Редактировать поле «${f.label || "без названия"}»`}
                            >
                              {f.label}
                            </button>
                            <button
                              type="button"
                              className="dealCustomFieldIconBtn dealCustomFieldIconBtnDanger"
                              onClick={() => handleDeleteCustomField(f)}
                              disabled={customFieldDefBusy}
                              title="Удалить поле"
                              aria-label="Удалить поле"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </div>
                          {multiline ? (
                            <DealCustomFieldTextArea
                              className={inputClass}
                              value={customFieldValues[f.id] ?? ""}
                              placeholder={f.label}
                              disabled={customFieldSaveBusy}
                              onChange={(e) => {
                                setCustomFieldValues((prev) => ({ ...prev, [f.id]: e.target.value }));
                                setCustomFieldErrors((prev) => ({ ...prev, [String(f.id)]: "" }));
                              }}
                              onBlur={(e) => saveCustomFieldValue(f.id, e.target.value)}
                            />
                          ) : (
                            <input
                              type={getCustomFieldInputType(f.type)}
                              className={inputClass}
                              value={customFieldValues[f.id] ?? ""}
                              onChange={(e) => {
                                setCustomFieldValues((prev) => ({ ...prev, [f.id]: e.target.value }));
                                setCustomFieldErrors((prev) => ({ ...prev, [String(f.id)]: "" }));
                              }}
                              onBlur={(e) => saveCustomFieldValue(f.id, e.target.value)}
                              placeholder={f.label}
                              disabled={customFieldSaveBusy}
                            />
                          )}
                        </div>
                        {fieldError ? (
                          <div className="dealFieldError dealCustomFieldFieldError">{fieldError}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
          </>
        )}

        {(tab === "main" || isDealerView) && (
          <div className="dealMainFooter">
            <div className="dealMainFooterPrimaryRow">
              {!isDealerView && data?.nextStageId ? (
                <button
                  type="button"
                  className="linkBtn dealNextStageBtn"
                  onClick={goNextStage}
                  disabled={nextStageBusy}
                  title={data.nextStageTitle ? `Перейти в «${data.nextStageTitle}»` : "Следующий этап"}
                >
                  {nextStageBusy ? "…" : "Следующий этап"}
                </button>
              ) : null}
              {!isDealerView && deal?.stageId !== "advance_received" ? (
                <button
                  type="button"
                  className="linkBtn dealMainFooterRefuseBtn"
                  onClick={() => openModal("refusal")}
                >
                  Отказ
                </button>
              ) : null}
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="section">
            <div className="historyTimeline">
              {history.length === 0 ? (
                <ListEmpty message="Нет записей" />
              ) : (
                history.map((h) => (
                  <div key={h.id} className="historyTimelineItem">
                    <div className="historyTimelineBody">
                      <div className="historyTimelineDate">{fmtDt(h.date, dealTz)}</div>
                      {h.type === "call" ? (
                        <>
                          <div className="historyCallTitleRow">
                            <div className="historyTimelineLabel">{h.label}</div>
                            {h.recId ? (
                              <button
                                type="button"
                                className="historyCallRecIdIcon"
                                data-rec-tooltip={`RecID: ${h.recId}`}
                                title={`RecID: ${h.recId}`}
                                aria-label={`RecID: ${h.recId}`}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="16" x2="12" y2="12" />
                                  <line x1="12" y1="8" x2="12.01" y2="8" />
                                </svg>
                              </button>
                            ) : null}
                          </div>
                          {h.meta || h.recordUrl ? (
                            <div className="historyCallMetaRow">
                              {h.meta ? <span className="historyTimelineMeta">{h.meta}</span> : null}
                              {h.recId && h.recordUrl ? (
                                <button
                                  type="button"
                                  className="historyCallRecordDownload"
                                  title="Скачать запись"
                                  disabled={telphinDownloadingRecId === String(h.recId)}
                                  aria-busy={telphinDownloadingRecId === String(h.recId)}
                                  onClick={() => downloadTelphinRecording(h.recId)}
                                >
                                  {telphinDownloadingRecId === String(h.recId) ? (
                                    <span className="spinner spinner-sm" aria-hidden="true" />
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                      <polyline points="7 10 12 15 17 10" />
                                      <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                  )}
                                  <span className="historyCallRecordDownloadLabel">
                                    {telphinDownloadingRecId === String(h.recId) ? "Загрузка…" : "Запись"}
                                  </span>
                                </button>
                              ) : h.recordUrl ? (
                                <a
                                  href={h.recordUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="historyCallRecordDownload"
                                  title="Открыть запись"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                  </svg>
                                  <span className="historyCallRecordDownloadLabel">Запись</span>
                                </a>
                              ) : null}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div className="historyTimelineLabel">{h.label}</div>
                      )}
                      {h.type === "document" && h.documentId != null ? (
                        <div className="historyDocumentRow">
                          <span className="historyTimelineMeta">{h.meta || "Документ"}</span>
                          <button
                            type="button"
                            className="historyDocumentDownloadIcon"
                            onClick={() => downloadDocument(h.documentId, h.meta)}
                            title="Скачать"
                            aria-label="Скачать"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </button>
                        </div>
                      ) : h.type !== "call" && h.meta ? (
                        <div className="historyTimelineMeta">{h.meta}</div>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {tab === "comments" && (
          <div className="section dealCommentsSection">
            <div className="dealCommentsList">
              {dealComments.length === 0 ? (
                <div className="dealCommentsEmpty">
                  <div className="dealCommentsEmptyIcon" aria-hidden="true">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <p className="dealCommentsEmptyText">Комментариев нет</p>
                  <p className="dealCommentsEmptyHint">Напишите комментарий в форме ниже</p>
                </div>
              ) : (
                dealComments.map((c) => (
                  <div key={c.id} className="dealCommentItem">
                    <div className="dealCommentText">{c.text}</div>
                    <div className="dealCommentMeta">
                      {c.authorName} · {fmtDt(c.created_at, dealTz)}
                    </div>
                  </div>
                ))
              )}
            </div>
            <DealCommentComposer
              dealId={id}
              onAdded={refetchDeal}
              onError={(msg) => showDealBanner(msg, "error")}
            />
          </div>
        )}

        {tab === "tasks" && (
          <div className="section">
            <div className="crmListSurface">
              <div className="crmGroupHeader crmGroupHeaderRow">
                <span>Задачи по сделке</span>
                <button
                  type="button"
                  className="dealTaskAddBtn"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 1);
                    d.setHours(10, 0, 0, 0);
                    setNewTaskDueAt(isoToLocalInputValue(d.toISOString(), dealTz));
                    setNewTaskTitle("");
                    setNewTaskDescription("");
                    openModal("createTask");
                  }}
                  title="Создать задачу"
                  aria-label="Создать задачу"
                >
                  +
                </button>
              </div>
              {tasksIncomplete.length === 0 ? (
                <div className="dealTasksEmpty">
                  <div className="dealTasksEmptyIcon" aria-hidden="true">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                  </div>
                  <p className="dealTasksEmptyText">Задач нет</p>
                  <p className="dealTasksEmptyHint">Создайте задачу кнопкой выше или она появится при смене этапа</p>
                </div>
              ) : (
                <>
                  {tasksNoDue.length > 0 && (
                    <div className="dealTasksBlock dealTasksBlockNoDue">
                      <div className="dealTasksBlockTitle">Без срока</div>
                      {tasksNoDue.map((t) => (
                        <CrmRow
                          key={t.id}
                          barClass="bad"
                          title={t.title}
                          right="Без срока"
                          onClick={() => openModal("task", t.id)}
                        />
                      ))}
                    </div>
                  )}
                  {tasksWithDue.length > 0 && (
                    <div className="dealTasksBlock">
                      {tasksNoDue.length > 0 && <div className="dealTasksBlockTitle">По срокам</div>}
                      {tasksWithDue.map((t) => (
                        <CrmRow
                          key={t.id}
                          barClass="warn"
                          title={t.title}
                          right={fmtDt(t.dueAt, dealTz)}
                          onClick={() => openModal("task", t.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        </div>
      </main>
      <ClientCardModal
        open={clientCardOpen}
        onClose={closeModal}
        userId={deal?.user_id != null ? String(deal.user_id) : undefined}
        useTelegramId={false}
      />
      <BottomSheetModal
        open={showTaskForm}
        onClose={() => {
          closeModal();
          setNewTaskTitle("");
          setNewTaskDescription("");
          setNewTaskDueAt("");
        }}
        title="Создать задачу"
      >
        <form
          className="dealTaskForm dealTaskFormInModal"
          onSubmit={handleCreateTask}
        >
          <input
            type="text"
            className="input"
            placeholder="Название задачи"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            autoFocus
          />
          <textarea
            className="input dealTaskDescription"
            placeholder="Описание (необязательно)"
            value={newTaskDescription}
            onChange={(e) => setNewTaskDescription(e.target.value)}
            rows={3}
          />
          <input
            type="datetime-local"
            className="input"
            value={newTaskDueAt}
            onChange={(e) => setNewTaskDueAt(e.target.value)}
            title="Срок (необязательно — можно установить позже)"
          />
          <div className="dealTaskFormActions">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn"
              disabled={taskSubmitBusy || !newTaskTitle.trim()}
            >
              {taskSubmitBusy ? "…" : "Создать"}
            </button>
          </div>
        </form>
      </BottomSheetModal>
      <BottomSheetModal
        open={qualificationOpen}
        onClose={() => {
          closeModal();
          setPendingStageId("");
        }}
        title="Квалификация"
      >
        <form
          className="qualificationForm"
          onSubmit={handleQualificationSubmit}
        >
          <div className="qualificationFormScroll">
            <div className="qualificationRow qualificationRowTwo">
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Бюджет</span>
                <input
                  type="text"
                  className={`input taskDetailTitleInput ${qualificationErrors.budget ? "inputError" : ""}`}
                  placeholder="Бюджет"
                  value={qualificationForm.budget}
                  onChange={(e) => {
                    setQualificationForm((prev) => ({ ...prev, budget: e.target.value }));
                    if (qualificationErrors.budget) setQualificationErrors((prev) => ({ ...prev, budget: "" }));
                  }}
                  required
                />
                {qualificationErrors.budget && (
                  <span className="qualificationFieldError">{qualificationErrors.budget}</span>
                )}
              </div>
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Сроки</span>
                <input
                  type="date"
                  className={`input taskDetailTitleInput ${qualificationErrors.deadlines ? "inputError" : ""}`}
                  value={qualificationForm.deadlines}
                  onChange={(e) => {
                    setQualificationForm((prev) => ({ ...prev, deadlines: e.target.value }));
                    if (qualificationErrors.deadlines) setQualificationErrors((prev) => ({ ...prev, deadlines: "" }));
                  }}
                  required
                />
                {qualificationErrors.deadlines && (
                  <span className="qualificationFieldError">{qualificationErrors.deadlines}</span>
                )}
              </div>
            </div>
            <div className="taskDetailRow">
              <span className="taskDetailLabel">Марка</span>
              <input
                type="text"
                className={`input taskDetailTitleInput ${qualificationErrors.brand ? "inputError" : ""}`}
                placeholder="Марка"
                value={qualificationForm.brand}
                onChange={(e) => {
                  setQualificationForm((prev) => ({ ...prev, brand: e.target.value }));
                  if (qualificationErrors.brand) setQualificationErrors((prev) => ({ ...prev, brand: "" }));
                }}
                required
              />
              {qualificationErrors.brand && (
                <span className="qualificationFieldError">{qualificationErrors.brand}</span>
              )}
            </div>
            <div className="qualificationRow qualificationRowTwo qualificationRowGapTop">
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Страна</span>
                <input
                  type="text"
                  className={`input taskDetailTitleInput ${qualificationErrors.country ? "inputError" : ""}`}
                  placeholder="Страна"
                  value={qualificationForm.country}
                  onChange={(e) => {
                    setQualificationForm((prev) => ({ ...prev, country: e.target.value }));
                    if (qualificationErrors.country) setQualificationErrors((prev) => ({ ...prev, country: "" }));
                  }}
                  required
                />
                {qualificationErrors.country && (
                  <span className="qualificationFieldError">{qualificationErrors.country}</span>
                )}
              </div>
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Год авто</span>
                <input
                  type="text"
                  className={`input taskDetailTitleInput ${qualificationErrors.year ? "inputError" : ""}`}
                  placeholder="Год"
                  value={qualificationForm.year}
                  onChange={(e) => {
                    setQualificationForm((prev) => ({ ...prev, year: e.target.value }));
                    if (qualificationErrors.year) setQualificationErrors((prev) => ({ ...prev, year: "" }));
                  }}
                  required
                />
                {qualificationErrors.year && (
                  <span className="qualificationFieldError">{qualificationErrors.year}</span>
                )}
              </div>
            </div>
            <div className="taskDetailRow">
              <span className="taskDetailLabel">Комплектация</span>
              <input
                type="text"
                className={`input taskDetailTitleInput ${qualificationErrors.trim ? "inputError" : ""}`}
                placeholder="Комплектация"
                value={qualificationForm.trim}
                onChange={(e) => {
                  setQualificationForm((prev) => ({ ...prev, trim: e.target.value }));
                  if (qualificationErrors.trim) setQualificationErrors((prev) => ({ ...prev, trim: "" }));
                }}
                required
              />
              {qualificationErrors.trim && (
                <span className="qualificationFieldError">{qualificationErrors.trim}</span>
              )}
            </div>
            <div className="qualificationRow qualificationRowTwo qualificationRowGapTop">
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Пробег</span>
                <input
                  type="text"
                  className={`input taskDetailTitleInput ${qualificationErrors.mileage ? "inputError" : ""}`}
                  placeholder="Пробег"
                  value={qualificationForm.mileage}
                  onChange={(e) => {
                    setQualificationForm((prev) => ({ ...prev, mileage: e.target.value }));
                    if (qualificationErrors.mileage) setQualificationErrors((prev) => ({ ...prev, mileage: "" }));
                  }}
                  required
                />
                {qualificationErrors.mileage && (
                  <span className="qualificationFieldError">{qualificationErrors.mileage}</span>
                )}
              </div>
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Состояние авто</span>
                <select
                  className={`input taskDetailTitleInput ${qualificationErrors.condition ? "inputError" : ""}`}
                  value={qualificationForm.condition}
                  onChange={(e) => {
                    setQualificationForm((prev) => ({ ...prev, condition: e.target.value }));
                    if (qualificationErrors.condition) setQualificationErrors((prev) => ({ ...prev, condition: "" }));
                  }}
                  required
                >
                  <option value="">Выберите</option>
                  <option value="целая">Целая</option>
                  <option value="битая">Битая</option>
                  <option value="без разницы">Без разницы</option>
                </select>
                {qualificationErrors.condition && (
                  <span className="qualificationFieldError">{qualificationErrors.condition}</span>
                )}
              </div>
            </div>
            <div className="qualificationRow qualificationRowGapTop">
              <div className="taskDetailRow">
                <span className="taskDetailLabel">Комментарий (необязательно)</span>
                <textarea
                  className="input taskDetailTitleInput qualificationCommentInput"
                  placeholder="Ваш комментарий к квалификации"
                  value={qualificationForm.comment}
                  onChange={(e) =>
                    setQualificationForm((prev) => ({ ...prev, comment: e.target.value }))
                  }
                  rows={3}
                />
              </div>
            </div>
          </div>
          <div className="qualificationFormFooter">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn qualificationSubmitBtn"
              disabled={qualificationSubmitBusy}
            >
              {qualificationSubmitBusy ? "Сохранение…" : "Сохранить и перевести в этап"}
            </button>
          </div>
        </form>
      </BottomSheetModal>
      <BottomSheetModal
        open={modal === "qualificationView"}
        onClose={closeModal}
        title="Квалификация"
      >
        <div className="qualificationViewBody">
          {data?.qualification && (
            <>
              <div className="qualificationViewGrid">
                {(qualificationFields || []).map((f) => (
                  <div key={f.key} className="crmKvRow">
                    <div className="crmKvLabel">{f.label}</div>
                    <div className="crmKvValue">
                      {data.qualification[f.key] != null && data.qualification[f.key] !== ""
                        ? String(data.qualification[f.key])
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
              {data.qualification.comment != null && String(data.qualification.comment).trim() !== "" && (
                <div className="qualificationViewComment">
                  <div className="qualificationViewCommentLabel">Комментарий</div>
                  <div className="qualificationViewCommentText">{data.qualification.comment}</div>
                </div>
              )}
            </>
          )}
        </div>
      </BottomSheetModal>
      <BottomSheetModal
        open={modal === "addCustomField"}
        onClose={closeModal}
        title="Добавить поле"
      >
        <form className="addCustomFieldForm" onSubmit={handleAddCustomFieldSubmit}>
          <div className="taskDetailRow">
            <span className="taskDetailLabel">Название</span>
            <input
              type="text"
              className="input taskDetailTitleInput"
              placeholder="Например: Комментарий менеджера"
              value={newCustomFieldLabel}
              onChange={(e) => setNewCustomFieldLabel(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="taskDetailRow">
            <span className="taskDetailLabel">Тип</span>
            <select
              className="input taskDetailTitleInput"
              value={newCustomFieldType}
              onChange={(e) => setNewCustomFieldType(e.target.value)}
            >
              {CUSTOM_FIELD_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="qualificationFormFooter">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn qualificationSubmitBtn"
              disabled={addCustomFieldBusy || !newCustomFieldLabel.trim()}
            >
              {addCustomFieldBusy ? "Сохранение…" : "Добавить"}
            </button>
          </div>
        </form>
      </BottomSheetModal>
      <BottomSheetModal
        open={modal === "editCustomField"}
        onClose={() => {
          closeModal();
          setEditCustomFieldId(null);
        }}
        title="Редактировать поле"
      >
        <form className="addCustomFieldForm" onSubmit={handleEditCustomFieldSubmit}>
          <div className="taskDetailRow">
            <span className="taskDetailLabel">Название</span>
            <input
              type="text"
              className="input taskDetailTitleInput"
              placeholder="Название поля"
              value={editCustomFieldLabel}
              onChange={(e) => setEditCustomFieldLabel(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="taskDetailRow">
            <span className="taskDetailLabel">Тип</span>
            <select
              className="input taskDetailTitleInput"
              value={editCustomFieldType}
              onChange={(e) => setEditCustomFieldType(e.target.value)}
            >
              {CUSTOM_FIELD_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="qualificationFormFooter">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn qualificationSubmitBtn"
              disabled={customFieldDefBusy || !editCustomFieldLabel.trim()}
            >
              {customFieldDefBusy ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </form>
      </BottomSheetModal>
      <BottomSheetModal
        open={refusalOpen}
        onClose={() => {
          closeModal();
          setRefusalReasonId(null);
        }}
        title="Причина отказа"
      >
        <div className="refusalModalBody">
          {refusalReasons.length === 0 ? (
            <div className="refusalModalEmpty">Нет причин. Добавьте их в настройках.</div>
          ) : (
            <div className="refusalReasonsList">
              {refusalReasons.map((r) => (
                <label key={r.id} className="refusalReasonItem">
                  <input
                    type="radio"
                    name="refusalReason"
                    checked={refusalReasonId === r.id}
                    onChange={() => setRefusalReasonId(r.id)}
                  />
                  <span>{r.title}</span>
                </label>
              ))}
            </div>
          )}
          <div className="refusalModalActions">
            <button
              type="button"
              className="linkBtn dealTaskSubmitBtn"
              disabled={refusalBusy || refusalReasonId == null}
              onClick={handleRefuse}
            >
              {refusalBusy ? "…" : "Оформить отказ"}
            </button>
          </div>
        </div>
      </BottomSheetModal>
      <BottomSheetModal
        open={contractUploadOpen}
        onClose={() => {
          closeModal();
          setContractFile(null);
        }}
        title="Договор заключен"
      >
        <form className="dealContractUploadForm" onSubmit={handleContractUpload}>
          <p className="dealContractUploadHint">Загрузите подписанный договор (PDF или DOCX) для перехода на этап «Договор заключен».</p>
          <input
            type="file"
            className="input"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setContractFile(e.target.files?.[0] ?? null)}
          />
          {contractFile ? (
            <div className="dealContractUploadFileName">{contractFile.name}</div>
          ) : null}
          <div className="dealTaskFormActions">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn"
              disabled={contractUploadBusy || !contractFile}
            >
              {contractUploadBusy ? "Загрузка…" : "Загрузить и перейти на этап"}
            </button>
          </div>
        </form>
      </BottomSheetModal>
      <BottomSheetModal
        open={!!selectedTask}
        onClose={closeModal}
        title="Задача"
      >
        {selectedTask && (
          <div className="taskDetailBody">
            <div className="taskDetailRow">
              <span className="taskDetailLabel">Название</span>
              <input
                type="text"
                className="input taskDetailTitleInput"
                value={taskDetailTitle}
                onChange={(e) => setTaskDetailTitle(e.target.value)}
                placeholder="Название задачи"
              />
            </div>
            <div className="taskDetailRow">
              <span className="taskDetailLabel">Срок</span>
              <input
                type="datetime-local"
                className="input taskDetailDateInput"
                value={taskDetailDueAt}
                onChange={(e) => setTaskDetailDueAt(e.target.value)}
                title="Установите срок или оставьте пустым (Без срока)"
              />
            </div>
            <div className="taskDetailRow">
              <span className="taskDetailLabel">Описание</span>
              <textarea
                className="input taskDetailDescriptionInput"
                value={taskDetailDescription}
                onChange={(e) => setTaskDetailDescription(e.target.value)}
                placeholder="Описание (необязательно)"
                rows={3}
              />
            </div>
            {!taskDetailDone ? (
              <button
                type="button"
                className="linkBtn taskDetailDoneBtn"
                disabled={taskDetailSaveBusy || !taskDetailTitle.trim()}
                onClick={markTaskDone}
              >
                {taskDetailSaveBusy ? "…" : "Отметить выполненным"}
              </button>
            ) : null}
            <button
              type="button"
              className="linkBtn dealTaskSubmitBtn"
              disabled={taskDetailSaveBusy || !taskDetailTitle.trim()}
              onClick={saveTaskDetail}
            >
              {taskDetailSaveBusy ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        )}
      </BottomSheetModal>
      <TransferManagerSheet
        open={transferManagerOpen}
        onClose={closeModal}
        title="Передать сделку"
        hint="Только эта сделка и её задачи будут закреплены за выбранным менеджером."
        onTransferred={async (toManagerId) => {
          if (!id) return;
          await apiJson(`/api/transfer/deals/${encodeURIComponent(id)}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ to_manager_id: toManagerId }),
          });
          await refetchDeal();
        }}
      />
      <BottomSheetModal
        open={assignDealerOpen}
        onClose={() => {
          closeModal();
          setAssignDealerId(null);
        }}
        title="Назначить дилера"
      >
        <form className="dealAssignDealerForm" onSubmit={handleAssignDealer}>
          <p className="dealAssignDealerHint">Дилер получит доступ к клиенту и чату по этой сделке.</p>
          <select
            className="input dealDealerSelect"
            value={assignDealerId ?? ""}
            onChange={(e) => setAssignDealerId(e.target.value ? Number(e.target.value) : null)}
            aria-label="Выберите дилера"
          >
            <option value="">— Не назначен —</option>
            {dealersList.map((d) => (
              <option key={d.id} value={d.id}>
                {d.display_name || d.code || `ID${d.id}`}
              </option>
            ))}
          </select>
          <div className="dealTaskFormActions">
            <button
              type="submit"
              className="linkBtn dealTaskSubmitBtn"
              disabled={assignDealerBusy}
            >
              {assignDealerBusy ? "…" : "Сохранить"}
            </button>
          </div>
        </form>
      </BottomSheetModal>
    </>
  );
}

function DealCommentComposer({ dealId, onAdded, onError }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="dealCommentComposer">
      <textarea
        className="input dealCommentInput"
        placeholder="Добавить комментарий..."
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button
        type="button"
        className="linkBtn dealTaskSubmitBtn"
        disabled={busy || !(text || "").trim()}
        onClick={async () => {
          const t = (text || "").trim();
          if (!t || !dealId) return;
          setBusy(true);
          try {
            await apiJson(`/api/deals/${dealId}/comments`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text: t }),
            });
            setText("");
            onAdded?.();
          } catch (e) {
            onError?.(e?.message || "Не удалось добавить комментарий");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "…" : "Добавить"}
      </button>
    </div>
  );
}
